import {
  defineQuiz,
  type Answers,
  type Persona,
  type Question,
  type QuizDefinition,
  type QuizMeta,
} from "@/schema/quizSchema";
import type { ReactNode } from "react";

/**
 * Declarative threshold-scoring spec for a Linear Quiz.
 *
 * - `points`: per-question maps for single-choice answers (qid → value → points).
 *   A missing question or value contributes 0.
 * - `multiSelect`: per-question cap for multi answers — the number of *non-excluded*
 *   picks, capped at `cap`. Mirrors `Math.min(cap, picks.filter(p => p !== exclude).length)`.
 * - `thresholds`: `[minScore, personaKey]` pairs walked high→low; the first whose
 *   `minScore` the total meets wins.
 * - `fallback`: the persona key returned when the score is below every threshold.
 */
export interface LinearScoring {
  points: Record<string, Record<string, number>>;
  multiSelect?: Record<string, { cap: number; exclude?: string }>;
  thresholds: ReadonlyArray<readonly [number, string]>;
  fallback: string;
}

/** The serializable parts of a Linear Quiz (everything except the derived functions). */
export interface LinearQuizSpec {
  id: string;
  slug: string;
  tenant: string;
  meta: QuizMeta;
  questions: Question[];
  personas: Persona[];
  scoring: LinearScoring;
  /** Optional custom Result Surface (e.g. AURAL water report) — preserved verbatim. */
  renderResult?: (ctx: { answers: Answers; persona: Persona }) => ReactNode;
}

/** Sum the declared single-choice point maps over the given answers. */
function sumPoints(points: LinearScoring["points"], answers: Answers): number {
  let total = 0;
  for (const [qid, map] of Object.entries(points)) {
    const v = answers[qid];
    if (typeof v === "string") total += map[v] ?? 0;
  }
  return total;
}

/** Sum the capped, opt-out-excluded multi-select contributions. */
function sumMultiSelect(
  multiSelect: NonNullable<LinearScoring["multiSelect"]>,
  answers: Answers,
): number {
  let total = 0;
  for (const [qid, { cap, exclude }] of Object.entries(multiSelect)) {
    const picks = Array.isArray(answers[qid]) ? (answers[qid] as string[]) : [];
    const counted = exclude === undefined ? picks : picks.filter((p) => p !== exclude);
    total += Math.min(cap, counted.length);
  }
  return total;
}

/**
 * Build a Linear Quiz: a {@link QuizDefinition} whose `branching` is derived from
 * the `questions` order (no hand-maintained ORDER array) and whose `classifier`
 * is a declarative threshold sum (no hand-rolled point/threshold code).
 *
 * The output is a plain QuizDefinition, so the engine and validateQuiz are
 * unchanged. `defineQuiz` remains the escape hatch for non-linear shapes
 * (e.g. argmax scoring) — a Linear Quiz can still carry a custom `renderResult`.
 */
export function defineLinearQuiz(spec: LinearQuizSpec): QuizDefinition {
  const { scoring, questions, renderResult, ...rest } = spec;

  // Linear branching: advance to the next question in declaration order.
  // Unknown current id terminates (matches the prior `ORDER.indexOf === -1` rule).
  const order = questions.map((q) => q.id);
  const branching = (_answers: Answers, currentQuestionId: string): string | null => {
    const idx = order.indexOf(currentQuestionId);
    if (idx === -1) return null;
    return order[idx + 1] ?? null;
  };

  // Declarative threshold classifier: sum points, walk thresholds high→low, fall back.
  const ordered = [...scoring.thresholds].sort((a, b) => b[0] - a[0]);
  const classifier = (answers: Answers): string => {
    const score =
      sumPoints(scoring.points, answers) +
      (scoring.multiSelect ? sumMultiSelect(scoring.multiSelect, answers) : 0);
    for (const [min, persona] of ordered) {
      if (score >= min) return persona;
    }
    return scoring.fallback;
  };

  return defineQuiz({
    ...rest,
    questions,
    branching,
    classifier,
    ...(renderResult ? { renderResult } : {}),
  });
}
